import { observable, action, makeObservable } from 'mobx'
import { codeMapStore, service } from '.'
import { OtpVerifyMethod } from 'mobileApp/system/model/otp'
import { ErrorData } from 'mobileApp/system/model/common'

export enum Role {
  Empty = '',
  Admin = 'ROLE_ADMIN',
  SenYoung = 'ROLE_SENYOUNG',
  Chubb = 'ROLE_CHUBB',
}

export type AdminUser = {
  name: string
  token: string
  role: Role
  chgPwd: boolean
  isRemindPasswordChange: boolean
  email: string
  phone: string
}
const emptyUser: AdminUser = {
  name: '',
  token: '',
  role: Role.Empty,
  chgPwd: false,
  isRemindPasswordChange: false,
  email: '',
  phone: '',
}
const emptyRoles = { upper: 0, lower: 0, digit: 0, min: 0, special: 0 }

class AuthStore {
  @observable private _isLogin = false
  @observable private _currUser = { ...emptyUser }
  @observable private _isPass2FA = false
  private _pwdRules = emptyRoles
  private _userAccount = ''

  constructor() {
    makeObservable(this)
    const userString = localStorage.getItem('adminUser')
    const userAccount = localStorage.getItem('tkecAccount')
    const twoFactor = localStorage.getItem('is_pass_2FA')

    if (userString && userString !== 'null') {
      this._isLogin = true
      this._currUser = JSON.parse(userString)
    }
    if (userAccount && userAccount !== 'null') this._userAccount = userAccount

    if (twoFactor && twoFactor !== 'null') {
      this._isPass2FA = JSON.parse(twoFactor) === 'true'
    }
  }

  get isLogin() {
    return this._isLogin
  }
  get isPass2FA() {
    return this._isPass2FA
  }
  get currUser() {
    return this._currUser
  }
  get myRole() {
    return this._currUser.role
  }
  get pwdRules() {
    return this._pwdRules
  }

  get userAccount() {
    return this._userAccount
  } // 如果有登入過並選擇記憶帳號 Email 的話，自動填入帳號 Email，不需重複填入

  @action login = async (account: string, password: string, isPersistAccount?: boolean) => {
    const res = await service.login(account, password)

    if (res.status !== 200) {
      const errorData: ErrorData = await res.json()
      const { detail, message } = errorData

      throw new Error(`${detail ?? message}`)
    }

    const user: AdminUser = await res.json()

    if (user.chgPwd) throw new Error('您未於期限內變更密碼，已被限制登入，請點選「忘記密碼」重新設定')

    this._currUser = user
    this._isLogin = true

    localStorage.setItem('adminUser', JSON.stringify(user))
    localStorage.setItem('is_pass_2FA', JSON.stringify('false'))

    if (isPersistAccount) {
      this._userAccount = account
      localStorage.setItem('tkecAccount', account)
    }

    // 確保 codeMapList 完成後才算登入成功，避免後續元件在資料尚未載入時顯示空白
    try {
      await codeMapStore.getCodeMapList()
    } catch (err) {
      console.error('登入時取得 codeMapList 失敗', err)
    }

    return Promise.resolve()
  }

  @action pass2FA = () => {
    localStorage.setItem('is_pass_2FA', JSON.stringify('true'))
    this._isPass2FA = true
  }

  @action getOtpCode = async (type: OtpVerifyMethod) => {
    const res = await service.getOtpCode(type)

    if (res.status === 200) return

    const errorData: ErrorData = await res.json()
    const { detail, message } = errorData

    throw new Error(`${detail ?? message}`)
  }

  @action verifyOtpCode = async ({ type, otpCode }: { type: string; otpCode: string }) => {
    const res = await service.verifyOtpCode({ type, otpCode })

    if (res.status === 200) return true

    const errorData: ErrorData = await res.json()
    const { detail, message } = errorData

    throw new Error(`${detail ?? message}`)
  }

  @action logout = () => {
    localStorage.removeItem('adminUser')
    localStorage.removeItem('is_pass_2FA')
    this._isLogin = false
    this._currUser = { ...emptyUser }
    this._isPass2FA = false
  }

  @action loadPwdRules = async () => {
    const res = await service.getPwdRule()

    if (res.status === 200) {
      this._pwdRules = await res.json()
      return
    }

    const errorData: ErrorData = await res.json()
    const { detail, message } = errorData

    throw new Error(`${detail ?? message}`)
  }

  @action changePassword = async (password: string, newPassword: string) => {
    const res = await service.chgPwd(password, newPassword)

    if (res.status === 200) return

    const errorData: ErrorData = await res.json()
    const { detail, message } = errorData

    throw new Error(`${detail ?? message}`)
  }

  @action resetPassword = async (account: string) => {
    const res = await service.resetPwd(account)

    if (res.status === 200) return

    const errorData: ErrorData = await res.json()
    const { detail, message } = errorData

    throw new Error(`${detail ?? message}`)
  }
}

export const authStore = new AuthStore()
